import { Box, Flex, VisuallyHidden } from '@chakra-ui/react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { useModalState } from '~/atoms/ModalState'
import { useUserState } from '~/atoms/UserState'
import MarkReadModal from '~/components/MarkReadModal'
import NewCallModal from '~/components/NewCallModal'
import NewThreadModal from '~/components/NewThreadModal'
import PreviewModal from '~/components/PreviewModal'
import { PendingPbxSwitchListener } from '~/components/shared/PbxPicker'
import { SkipLink } from '~/components/shared/SkipLink'
import { ElectronLogger } from '~/components/specialized/ElectronLogger'
import { HybridCallManager } from '~/components/specialized/HybridCallManager'
import { Toaster } from '~/components/ui/toaster'
import { useToastAlerts } from '~/hooks/toast/useToastAlerts'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import useElectronIPC from '~/hooks/useElectronIPC'
import { useLeanPopupFlagSync } from '~/hooks/useLeanPopupFlagSync'
import {
  APP_NAME,
  MAIN_CONTENT_ID,
  PAGE_HEADING_ID,
  useRouteFocusAndTitle,
} from '~/hooks/useRouteFocusAndTitle'
import BannerManager from '~/pages/Home/Banners/BannerManager'
import { CallInProgressModal } from '~/pages/Home/Modals/CallInProgressModal/CallInProgressModal'
import ConfirmModal from '~/pages/Home/Modals/ConfirmModal/ConfirmModal'
import { NewExtensionSubscriptionModal } from '~/pages/Home/Modals/NewExtensionSubscriptionModal/NewExtensionSubscriptionModal'
import { NewFaxModal } from '~/pages/Home/Modals/NewFaxModal/NewFaxModal'
import { NewParkingLotModal } from '~/pages/Home/Modals/NewParkingLotModal/NewParkingLotModal'
import { SessionExpiredModal } from '~/pages/Home/Modals/SessionExpiredModal/SessionExpiredModal'
import { PbxQueryParamPbxSwitchListener } from '~/pages/Home/PbxQueryParamPbxSwitchListener'
import { TopNav } from '~/pages/Home/TopNav/TopNav'
import FeedbackReasonsModal from '~/pages/WebPhone/FeedbackReasonsModal'
import Logger from '~/services/Logger'
import { initializeAnalytics } from '~/utils/analytics'

import { MoveFaxModal } from './Modals/MoveFaxModal/MoveFaxModal'
import { NewSpeedDialModal } from './Modals/NewSpeedDialModal/NewSpeedDialModal'
import { SiteNavigation } from './SiteNavigation/SiteNavigation'

const logger = new Logger('HomePage')

export const HomePage = () => {
  const { sessionUser: user, pbx, currentUserExtension } = useUserState()
  const showTexting = useShowTexting()
  const { updateMenus } = useElectronIPC()
  const { t } = useTranslation()

  useToastAlerts()
  useLeanPopupFlagSync()

  const {
    confirmModal: [, setConfirmModalState],
    faxModal: [faxModalState, setFaxModalState],
  } = useModalState()

  useEffect(() => {
    if (user && pbx) {
      logger.debug(
        `Analytics initialized: ${user}, ${currentUserExtension}, ${pbx}`,
      )
      initializeAnalytics(
        user,
        currentUserExtension, // May be undefined if user hasn't activated their extension, which is fine
        pbx,
      )
    } else {
      logger.debug(`Analytics NOT initialized. Missing data: ${user}, ${pbx}`)
    }
  }, [user, currentUserExtension, pbx])

  const { pageName } = useRouteFocusAndTitle({
    pbxName: pbx?.name,
    mainContentId: MAIN_CONTENT_ID,
  })

  useEffect(() => {
    setFaxModalState({ ...faxModalState, open: false })
    setConfirmModalState(null)
    updateMenus({ enableAuthedMenuItems: true })
  }, [])

  return (
    <Box
      width="full"
      height="full"
      position="relative"
      overflow-y="hidden"
      bg="bg.muted"
    >
      <SkipLink targetId={MAIN_CONTENT_ID}>
        {t('nav.skipToMainContent', 'Skip to main content')}
      </SkipLink>
      <PbxQueryParamPbxSwitchListener />
      <PendingPbxSwitchListener />
      <Toaster />

      <ToastContainer
        autoClose={5000}
        pauseOnFocusLoss={false}
        hideProgressBar
        closeOnClick
        newestOnTop
      />
      <Flex direction="column" height="full">
        <BannerManager />
        <TopNav />
        <Flex
          direction="row"
          flex="1"
          minHeight={0}
          overflowX={'auto'}
          overflowY={'hidden'}
        >
          <SiteNavigation />
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              flex: 1,
              minHeight: 0,
              position: 'relative',
              backgroundColor: 'var(--chakra-colors-bg-muted)',
            }}
          >
            <main
              id={MAIN_CONTENT_ID}
              tabIndex={-1}
              style={{
                display: 'flex',
                flex: 1,
                minHeight: 0,
                position: 'relative',
                outline: 'none',
              }}
            >
              <VisuallyHidden as="h1" id={PAGE_HEADING_ID} tabIndex={-1}>
                {pageName ?? APP_NAME}
              </VisuallyHidden>
              <Outlet />
            </main>
          </div>
        </Flex>
      </Flex>

      <ElectronLogger />
      <HybridCallManager />

      {/**
       * Modals
       */}

      <CallInProgressModal />
      <ConfirmModal />
      <MarkReadModal />
      <NewCallModal />
      <NewFaxModal />
      <MoveFaxModal />
      <NewParkingLotModal />
      <NewSpeedDialModal />
      <NewExtensionSubscriptionModal />
      {showTexting && <NewThreadModal />}
      <PreviewModal />
      <FeedbackReasonsModal />
      <SessionExpiredModal />
    </Box>
  )
}

export default HomePage
